import React, { useState } from 'react';
import { Clock, Lock, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { lawyerData, CONTACT_AREA_OPTIONS } from '../data/mock';
import { formatPhoneMask } from '../lib/utils';
import { toast } from 'sonner';
import { useParallaxInView } from '../hooks/useParallax';

export const FormSection = () => {
  const { ref: introRef, style: introParallax } = useParallaxInView(0.045);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [area, setArea] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'phone' ? formatPhoneMask(value) : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!area) {
      toast.error('Selecione a área do problema.');
      return;
    }

    setIsSubmitting(true);

    const areaLabel =
      CONTACT_AREA_OPTIONS.find((o) => o.value === area)?.label || area;
    const subject = `Análise inicial — ${areaLabel}`;

    try {
      const response = await fetch(
        'https://formsubmit.co/ajax/df50e6e2b8b5c9bcd20ce1bc66c1af77',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            area: areaLabel,
            message: formData.message,
            _subject: subject,
            _template: 'table',
            _captcha: false
          })
        }
      );

      const result = await response.json().catch(() => ({}));
      const failed =
        !response.ok || result.success === 'false' || result.success === false;
      const apiMessage = typeof result.message === 'string' ? result.message : '';

      if (failed) {
        if (/activation/i.test(apiMessage)) {
          toast.error('Ative o formulário pelo e-mail.', {
            description:
              'Abra a caixa de entrada (e o spam), clique em “Activate Form” e tente de novo.'
          });
          return;
        }
        throw new Error(apiMessage || 'Falha no envio');
      }

      toast.success('Solicitação enviada com sucesso.', {
        description: 'Entraremos em contato em breve.'
      });

      setFormData({ name: '', email: '', phone: '', message: '' });
      setArea('');
    } catch {
      toast.error('Não foi possível enviar. Tente novamente ou use o WhatsApp.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="analise" className="relative overflow-hidden bg-[#f5f1ed] py-16 pb-24 sm:py-20 sm:pb-20 lg:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        <div className="grid items-start gap-10 lg:grid-cols-2 lg:gap-16">
          <div className="lg:pt-4" ref={introRef} style={introParallax.style}>
            <h2 className="mb-5 font-serif text-2xl leading-tight tracking-tight text-[#0f1f3d] sm:text-3xl md:text-4xl">
              {lawyerData.form.title}
            </h2>
            <p className="text-slate-600 leading-relaxed text-sm sm:text-base mb-10">
              {lawyerData.form.description}
            </p>
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-[#b8962e] shrink-0" />
              <p className="text-sm sm:text-base text-[#0f1f3d] font-medium">
                {lawyerData.form.responseTime}
              </p>
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-md p-6 sm:p-8 border border-slate-200/80 shadow-sm"
          >
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="name" className="text-slate-700 text-sm">
                  Nome completo
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="bg-white border-slate-200 rounded-md h-11"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="phone" className="text-slate-700 text-sm">
                  WhatsApp
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(00) 9999-9999"
                  maxLength={16}
                  required
                  className="bg-white border-slate-200 rounded-md h-11"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-slate-700 text-sm">
                  E-mail
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="bg-white border-slate-200 rounded-md h-11"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="form-area" className="text-slate-700 text-sm">
                  Área do problema
                </Label>
                <Select value={area} onValueChange={setArea}>
                  <SelectTrigger id="form-area" className="h-11 bg-white border-slate-200 rounded-md">
                    <SelectValue placeholder="Selecione uma opção" />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTACT_AREA_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="message" className="text-slate-700 text-sm">
                  Descreva brevemente seu caso
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  required
                  className="bg-white border-slate-200 rounded-md resize-none"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full mt-6 bg-[#0f1f3d] hover:bg-[#1a2f4d] text-white rounded-md py-6 text-base font-semibold disabled:opacity-70"
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Enviando…
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Send className="w-5 h-5" />
                  Enviar solicitação
                </span>
              )}
            </Button>

            <p className="flex items-center gap-2 text-xs text-slate-500 mt-4 justify-center">
              <Lock className="w-3.5 h-3.5 shrink-0" />
              {lawyerData.form.privacyNote}
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};
